import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { PlusIcon, RefreshCwIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { EntryList } from '@/lib/api/credit-conversion-settings';
import { categoryLabels, type Category } from '@/lib/credit-conversion-settings/tables';

import type { Load } from './useConversionSettings';

const categoryColors: Record<Category, 'info' | 'success' | 'default'> = {
  scheduled: 'info',
  active: 'success',
  past: 'default',
};

export function CategoryChip({ category }: { category: Category }) {
  return <Chip size="small" label={categoryLabels[category]} color={categoryColors[category]} />;
}

export interface EntryListPanelProps {
  list: Load<EntryList>;
  selectedDate: string | null;
  /** 編集モードの間と、保存・取得の結果を待つ間は操作できない */
  disabled: boolean;
  /** 値の取得中は「追加」を押せない。編集モードにした後に取得が終わると、選択し直して URL を書き換えることがあるため */
  valueLoading: boolean;
  onSelect: (date: string) => void;
  onCreate: () => void;
  onReload: () => void;
}

/** 適用開始日の一覧（新しい順）と「追加」ボタン */
export function EntryListPanel({
  list,
  selectedDate,
  disabled,
  valueLoading,
  onSelect,
  onCreate,
  onReload,
}: EntryListPanelProps) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Box display="flex" alignItems="baseline" gap={2}>
          <Typography variant="subtitle1" fontWeight="bold">
            適用開始日
          </Typography>
          {list.status === 'ok' && (
            <Typography variant="body2" color="text.secondary">
              当日：{list.data.today}
            </Typography>
          )}
        </Box>
        <Button
          size="sm"
          onClick={onCreate}
          disabled={disabled || list.status !== 'ok' || valueLoading}
        >
          <PlusIcon />
          追加
        </Button>
      </Box>
      {list.status === 'loading' && (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={24} />
        </Box>
      )}
      {list.status === 'error' && (
        <Alert
          severity="error"
          action={
            <Button size="sm" variant="outline" onClick={onReload} disabled={disabled}>
              <RefreshCwIcon />
              再読み込み
            </Button>
          }
        >
          {list.message}
        </Alert>
      )}
      {list.status === 'ok' && list.data.entries.length === 0 && (
        <Typography color="text.secondary" variant="body2">
          この設定項目には、まだ値がありません。
        </Typography>
      )}
      {list.status === 'ok' && list.data.entries.length > 0 && (
        <List
          dense
          disablePadding
          // 一覧の高さは、画面が高いほど伸ばして多くの日付を示す。870px は、一覧より上の部分と、
          // 行の最も多い設定項目（消費項目別の係数）の値の表示の高さで、その表示を画面の中に残す
          sx={{ maxHeight: 'max(240px, calc(100vh - 870px))', overflowY: 'auto' }}
          aria-label="適用開始日の一覧"
        >
          {list.data.entries.map((entry) => (
            <ListItemButton
              key={entry.effective_dt}
              selected={entry.effective_dt === selectedDate}
              disabled={disabled}
              onClick={() => onSelect(entry.effective_dt)}
            >
              <ListItemText primary={entry.effective_dt} sx={{ flex: '0 0 auto', mr: 2 }} />
              <CategoryChip category={entry.category} />
            </ListItemButton>
          ))}
        </List>
      )}
    </Paper>
  );
}
